import { useState } from 'react';
import { Button, DialogFooter, Slider } from '@sandtech-hub-monorepo/ui-kit';
import Cropper from 'react-easy-crop';
import { ZoomInIcon, ZoomOutIcon } from 'lucide-react';

interface Props {
  aspectRatio?: number;
  imageUrl: string;
  onCrop: (image: File) => void;
  onDismiss: () => void;
}

/**
 * Copied from central-hub's Profile/Edit/ImageCropper (not a shared
 * component) so the two apps stay independently editable.
 */
export const ImageCropper = ({
  imageUrl,
  onCrop,
  onDismiss,
  aspectRatio = 1,
}: Props) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [area, setArea] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [zoom, setZoom] = useState(1);

  const executeCrop = () => {
    const canvas = document.createElement('canvas');
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      canvas.width = 400;
      canvas.height = 400;

      ctx!.drawImage(
        img,
        (area.x * img.width) / 100,
        (area.y * img.height) / 100,
        (area.width * img.width) / 100,
        (area.height * img.height) / 100,
        0,
        0,
        400,
        400
      );

      canvas.toBlob(async (blob) => {
        const file = new File([blob!], 'image.png', { type: 'image/png' });
        onCrop(file);
      });
    };
  };

  return (
    <div className="flex flex-col flex-center gap-4">
      <div className="max-w-[400px] w-full pb-[100%] self-center relative">
        <div className="w-full h-full absolute">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropAreaChange={(area) => setArea(area)}
          />
        </div>
      </div>
      <div className="flex gap-3 w-[300px] self-center">
        <ZoomOutIcon
          className="text-primary cursor-pointer"
          size={32}
          onClick={() => setZoom((z) => Math.max(1, z - 0.25))}
        />
        <Slider
          min={1}
          max={3}
          step={0.01}
          value={[zoom]}
          onValueChange={([z]) => setZoom(z)}
        />
        <ZoomInIcon
          className="text-primary cursor-pointer"
          size={32}
          onClick={() => setZoom((z) => Math.min(3, z + 0.25))}
        />
      </div>
      <DialogFooter>
        <Button onClick={onDismiss} variant="outline">
          Dismiss
        </Button>
        <Button onClick={executeCrop}>Crop</Button>
      </DialogFooter>
    </div>
  );
};
